(function () {
  function q(sel, root) { return (root || document).querySelector(sel); }
  function qa(sel, root) { return Array.from((root || document).querySelectorAll(sel)); }

  function getZonedNowParts(tz) {
    const fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: tz, year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', hour12: false, weekday: 'short'
    });
    const parts = fmt.formatToParts(new Date()).reduce((acc, p) => (acc[p.type] = p.value, acc), {});
    return {
      year: +parts.year,
      month: +parts.month, // 1-12
      day: +parts.day,     // 1-31
      hour: +parts.hour,
      minute: +parts.minute,
      weekdayShort: (parts.weekday || '').toUpperCase() // "MON"
    };
  }

  function formatZoned(date, tz) {
    // "MON 2nd of Jan, 2025"
    const weekday = new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: tz }).format(date);
    const d = new Intl.DateTimeFormat('en-GB', { day: 'numeric', timeZone: tz }).format(date);
    const m = new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: tz }).format(date);
    const y = new Intl.DateTimeFormat('en-GB', { year: 'numeric', timeZone: tz }).format(date);
    const ordinal = (n) => {
      const s = ["th","st","nd","rd"], v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    return `${weekday} ${ordinal(Number(d))} of ${m}, ${y}`;
  }

  function addDays(date, days) {
    const d = new Date(date);
    d.setUTCDate(d.getUTCDate() + days);
    return d;
  }

  function makeUTCDateFromZoned(y, m1, d, tz) {
    // Build a Date corresponding to y-m1-d 12:00 in tz, then convert to UTC Date
    // Using midday avoids DST edge issues when later formatting with TZ.
    const z = new Date(Date.UTC(y, m1 - 1, d, 12, 0, 0));
    // We don't shift here; we only use TZ when formatting / reading weekday.
    return z;
  }

  function weekdayInTZ(date, tz) {
    // 0=Sun ... 6=Sat (in the target tz)
    const fmt = new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone: tz });
    const map = { SUN:0, MON:1, TUE:2, WED:3, THU:4, FRI:5, SAT:6 };
    const w = fmt.format(date).toUpperCase();
    return map[w] ?? 0;
  }

  // Returns a Set of "YYYY-MM-DD" strings from a newline/comma-separated attribute value.
  function parseBlockedDates(raw) {
    if (!raw) return new Set();
    return new Set(
      raw.split(/[\n,]+/)
         .map(s => s.trim())
         .filter(s => /^\d{4}-\d{2}-\d{2}$/.test(s))
    );
  }

  // Returns the date as "YYYY-MM-DD" in the given timezone, for blocked-date comparison.
  function toYMD(date, tz) {
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
  }

  function isBlocked(date, tz, blockedDates) {
    return blockedDates.size > 0 && blockedDates.has(toYMD(date, tz));
  }

  function nextProcessingDate(startDateUTC, tz, processingDays, cutoffPassedToday, blockedDates) {
    // startDateUTC is "today" at ~noon UTC proxy for the zone day
    // If today is a processing day AND cutoff not passed AND not blocked, return today.
    // Else, move to next date that is an allowed processing day.
    let candidate = new Date(startDateUTC);
    const todayW = weekdayInTZ(candidate, tz);
    if (processingDays[todayW] && !cutoffPassedToday && !isBlocked(candidate, tz, blockedDates)) {
      return candidate;
    }
    // advance until we find an allowed, unblocked day
    for (let i = 1; i <= 30; i++) {
      candidate = addDays(startDateUTC, i);
      const w = weekdayInTZ(candidate, tz);
      if (processingDays[w] && !isBlocked(candidate, tz, blockedDates)) return candidate;
    }
    return addDays(startDateUTC, 1); // fallback
  }

  function addBusinessDays(startUTC, tz, numDays, includeWeekends, blockedDates) {
    if (numDays <= 0) return startUTC;
    if (includeWeekends && blockedDates.size === 0) return addDays(startUTC, numDays);
    let d = new Date(startUTC);
    let added = 0;
    while (added < numDays) {
      d = addDays(d, 1);
      const w = weekdayInTZ(d, tz);
      if (!includeWeekends && (w === 0 || w === 6)) continue;
      if (isBlocked(d, tz, blockedDates)) continue;
      added++;
    }
    return d;
  }

  function computeEstimatedDate(el) {
    const tz = el.getAttribute('data-tz') || 'Europe/London';
    const cutoffHour = parseInt(el.getAttribute('data-cutoff-hour') || '14', 10);
    const cutoffMinute = parseInt(el.getAttribute('data-cutoff-minute') || '0', 10);
    const includeWeekends = String(el.getAttribute('data-include-weekends')).toLowerCase() === 'true';
    const deliveryDays = parseInt(el.getAttribute('data-delivery-days') || '2', 10);
    const blockedDates = parseBlockedDates(el.getAttribute('data-blocked-dates'));

    const procDays = {
      // 0=Sun ... 6=Sat
      0: String(el.getAttribute('data-proc-sun')).toLowerCase() === 'true',
      1: String(el.getAttribute('data-proc-mon')).toLowerCase() === 'true',
      2: String(el.getAttribute('data-proc-tue')).toLowerCase() === 'true',
      3: String(el.getAttribute('data-proc-wed')).toLowerCase() === 'true',
      4: String(el.getAttribute('data-proc-thu')).toLowerCase() === 'true',
      5: String(el.getAttribute('data-proc-fri')).toLowerCase() === 'true',
      6: String(el.getAttribute('data-proc-sat')).toLowerCase() === 'true',
    };

    // "Now" in the shop's timezone
    const nowParts = getZonedNowParts(tz);
    const afterCutoff =
      (nowParts.hour > cutoffHour) ||
      (nowParts.hour === cutoffHour && nowParts.minute >= cutoffMinute);

    // Build a UTC date representing "today" in shop tz for stable weekday checks
    const todayUTC = makeUTCDateFromZoned(nowParts.year, nowParts.month, nowParts.day, tz);

    // Processing date
    const processingUTC = nextProcessingDate(todayUTC, tz, procDays, afterCutoff, blockedDates);

    // Delivery date (processing date + N days, possibly skipping weekends and blocked dates)
    const deliveryUTC = addBusinessDays(processingUTC, tz, deliveryDays, includeWeekends, blockedDates);

    // Write out
    const out = q('.estimated-delivery-date', el);
    if (out) out.textContent = formatZoned(deliveryUTC, tz);
  }

  function init() {
    qa('[data-role="estimated-delivery"]').forEach(computeEstimatedDate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
